/**
 * LiveDot -- Pulsing coral dot with t("component.live") mono-label.
 *
 * The dot pulses via the `pulse-dot` keyframes declared in
 * `apps/dashboard/src/styles/motion.css`. The animation is gated by
 * the global prefers-reduced-motion fallback in motion.css; the
 * `useShimmer` hook further gates the className application so the
 * dot is static when the user prefers reduced motion.
 *
 * Migrated from inline animation to .motion__pulse-dot utility class
 * by T304 (FR-002).
 *
 * @see apps/dashboard/src/styles/motion.css
 */
import React from "react";
import { useShimmer } from "../skeletons/useShimmer";
import { useT } from "../../../i18n/useT";

export interface LiveDotProps {
  /** Custom label content. Defaults to t("component.live"). */
  children?: React.ReactNode;
}

export function LiveDot({ children }: LiveDotProps) {
  const t = useT();

  const animationEnabled = useShimmer();

  return (
    <span
      data-testid="live-dot"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "var(--space-xs)",
        font: "var(--fs-mono)/1.4 var(--font-mono)",
        color: "var(--color-coral)",
        textTransform: "uppercase",
        letterSpacing: "0.28px",
      }}
    >
      <span
        className={animationEnabled ? "motion__pulse-dot" : undefined}
        style={{
          display: "inline-block",
          width: "8px",
          height: "8px",
          borderRadius: "var(--radius-full)",
          background: "var(--color-coral)",
        }}
      />
      {children ?? t("component.live")}
    </span>
  );
}

export default LiveDot;
